import type { Property } from '@/core/api/models/profile';
import type { UnitInstallmentsMap } from '@/core/api/models/unitInstallments';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import { shouldInvokeSkipPasscodeApi } from '@/modules/passcode/utils/skipPasscodeGate';
import { useCallback, useRef, useState } from 'react';
import { unitInstallmentsService } from '../services/unitInstallmentsService';

// Progressive rendering configuration
const IMMEDIATE_UPDATE_COUNT = 5; // First 2-5 units update immediately
const BATCH_SIZE = 15; // Remaining units batched in groups
const BATCH_DEBOUNCE_MS = 150; // Debounce delay for batched updates

/**
 * Custom hook for fetching unit installments in parallel for all units
 * Handles parallel API calls and updates appStore
 */
export const useUnitInstallments = () => {
    const [error, setError] = useState<string | null>(null);
    const isFetchingRef = useRef(false); // Prevent concurrent fetches
    const hasFetchedRef = useRef(false); // Track if we've fetched at least once

    // Get store state and actions - only get stable selectors
    const setUnitInstallments = useAppStore((state) => state.setUnitInstallments);
    const setUnitInstallmentsLoading = useAppStore((state) => state.setUnitInstallmentsLoading);

    /**
     * Fetch unit installments for all properties in parallel
     * CRITICAL: This function must NOT depend on properties or customerId in dependencies
     * to prevent infinite loops. Read them directly from store inside the function.
     */
    const fetchAllUnitInstallments = useCallback(async (): Promise<void> => {
        // Prevent concurrent fetches
        if (isFetchingRef.current) {
            logger.debug('useUnitInstallments:Skip fetch - already fetching');
            return;
        }

        // Read current state from store (not from dependencies)
        const currentState = useAppStore.getState();
        const isAuth = currentState.auth.isAuthenticated;

        if (currentState.isAdhaJourney) {
            logger.debug('useUnitInstallments:Skip fetch - ADHA journey');
            return;
        }
        const properties = currentState.properties;

        // Authentication guard: prevent API calls when logged out
        if (!isAuth) {
            logger.debug('useUnitInstallments:Skip fetch - user not authenticated');
            return;
        }

        // Email/password only: wait for passcodeToken. Converted UAE Pass /
        // Emirates ID leads have no passcode flow — proceed with JWT alone.
        const passcodeToken = currentState.auth.passcodeToken;
        const loginToken = currentState.auth.token;
        if (
            shouldInvokeSkipPasscodeApi() &&
            !passcodeToken &&
            loginToken
        ) {
            logger.debug('useUnitInstallments:Skip fetch - passcode setup in progress (waiting for passcode token)');
            return;
        }

        // Guard: need properties
        if (!properties || properties.length === 0) {
            logger.debug('useUnitInstallments:Skip fetch - no properties');
            return;
        }

        // Always fetch fresh data - don't skip if installments exist
        // This ensures we get the latest installment data from the API
        isFetchingRef.current = true;

        try {
            setError(null);
            setUnitInstallmentsLoading(true);

            logger.info('useUnitInstallments:Fetching installments for all units (progressive)', {
                unitCount: properties.length,
                immediateCount: IMMEDIATE_UPDATE_COUNT,
                batchSize: BATCH_SIZE,
            });

            // Track processed units and pending batch
            const processedUnitIds = new Set<string>();
            const pendingBatch: Array<{ unitId: string; installments: any }> = [];
            let batchTimeout: ReturnType<typeof setTimeout> | null = null;
            let completedCount = 0;
            const totalUnits = properties.length;

            // Debounced batch update function
            const flushBatch = () => {
                if (pendingBatch.length === 0) return;

                const batchMap: UnitInstallmentsMap = {};
                pendingBatch.forEach((item) => {
                    batchMap[item.unitId] = item.installments;
                });

                // Merge with existing installments
                setUnitInstallments(batchMap, true);

                logger.debug('useUnitInstallments:Batched update', {
                    batchSize: pendingBatch.length,
                    totalProcessed: processedUnitIds.size,
                });

                pendingBatch.length = 0;
            };

            // Schedule batch flush with debounce
            const scheduleBatchFlush = () => {
                if (batchTimeout) {
                    clearTimeout(batchTimeout);
                }
                batchTimeout = setTimeout(flushBatch, BATCH_DEBOUNCE_MS);
            };

            // Fetch installments for all units in parallel
            const installmentsPromises = properties.map(async (property: Property, index: number) => {
                try {
                    const response = await unitInstallmentsService.getUnitInstallments(
                        property.salesOrderId
                    );
                    
                    const result = {
                        unitId: property.id,
                        installments: response,
                    };

                    completedCount++;
                    const isImmediate = index < IMMEDIATE_UPDATE_COUNT;

                    if (isImmediate) {
                        // Immediate update for first N units
                        const immediateMap: UnitInstallmentsMap = {
                            [property.id]: response,
                        };
                        setUnitInstallments(immediateMap, true);
                        processedUnitIds.add(property.id);

                        logger.debug('useUnitInstallments:Immediate update', {
                            unitId: property.id,
                            index,
                        });
                    } else {
                        // Add to batch for remaining units
                        pendingBatch.push(result);
                        processedUnitIds.add(property.id);
                        scheduleBatchFlush();
                    }

                    // If all units completed, flush any remaining batch
                    if (completedCount === totalUnits) {
                        if (batchTimeout !== null) {
                            clearTimeout(batchTimeout);
                            batchTimeout = null;
                        }
                        flushBatch();
                    }

                    return result;
                } catch (err) {
                    completedCount++;
                    logger.error('useUnitInstallments:Failed to fetch installments for unit', {
                        unitId: property.id,
                        error: err,
                    });
                    // If all units completed (including failures), flush batch
                    if (completedCount === totalUnits) {
                        if (batchTimeout !== null) {
                            clearTimeout(batchTimeout);
                            batchTimeout = null;
                        }
                        flushBatch();
                    }
                    return null;
                }
            });

            // Start all requests (they update progressively as they complete)
            await Promise.allSettled(installmentsPromises);

            // Final flush to ensure all pending items are updated
            if (batchTimeout !== null) {
                clearTimeout(batchTimeout);
                batchTimeout = null;
            }
            flushBatch();

            logger.info('useUnitInstallments:Installments fetched successfully (progressive)', {
                totalProcessed: processedUnitIds.size,
                totalUnits: properties.length,
            });

            hasFetchedRef.current = true;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch unit installments';
            setError(errorMessage);
            logger.error('useUnitInstallments:Failed to fetch installments', err);
            throw err;
        } finally {
            setUnitInstallmentsLoading(false);
            isFetchingRef.current = false;
        }
    }, [setUnitInstallments, setUnitInstallmentsLoading]);

    /**
     * Retry fetching installments (clears error and calls fetchAllUnitInstallments)
     */
    const retry = useCallback(async (): Promise<void> => {
        setError(null);
        await fetchAllUnitInstallments();
    }, [fetchAllUnitInstallments]);

    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Get loading state from store (not as dependency)
    const isLoading = useAppStore((state) => state.unitInstallmentsLoading);

    return {
        // State
        isLoading,
        error,

        // Actions
        fetchAllUnitInstallments,
        retry,
        clearError,
    };
};

